import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { OidcService } from '../services/oidc.service';
import { UserService } from '../services/user.service';
import { TenantContextService } from '../services/tenant-context.service';
import { catchError, filter, map, of, switchMap, take } from 'rxjs';

// Guards routes by 'allowedRoles'/'requiredLicense' route data, checked inside the same auth-gated pipe.
export const roleBasedGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const userService = inject(UserService);
  const oidcService = inject(OidcService);
  const tenantContextService = inject(TenantContextService);
  const router = inject(Router);

  const allowedRoles = route.data['allowedRoles'] as string[] | undefined;
  const requiredLicense = route.data['requiredLicense'] as string | undefined;

  // If no allowedRoles and no requiredLicense specified, allow access
  if ((!allowedRoles || allowedRoles.length === 0) && !requiredLicense) {
    return true;
  }

  return oidcService.checkAuthDone$.pipe(
    filter((checkAuthDone) => !!checkAuthDone),
    take(1),
    switchMap(() => {
      if (!oidcService.isAuthenticated$.value) {
        // Not authenticated - AutoLoginByConfigIdGuard owns this case (login redirect).
        return of(true);
      }

      // This guard can run before ShellLayoutComponent mounts, so ensure tenant context is loaded first.
      return userService.ensureTenantContext().pipe(
        catchError(() => of(undefined)),
        switchMap(() => {
          // License check first (fail fast) - an unlicensed tenant is denied regardless of role.
          if (requiredLicense && !tenantContextService.hasLicense(requiredLicense)) {
            router.navigate(['/educator/unauthorized'], { state: { reason: 'unauthorized' } });
            return of(false);
          }

          if (!allowedRoles || allowedRoles.length === 0) {
            return of(true);
          }

          // Use Observable approach - safe, reliable, and still uses cached data (no extra API calls)
          return userService.getUserRoles().pipe(
            map((roles) => {
              const hasAccess = roles.some(role => allowedRoles.includes(role));

              if (hasAccess) {
                return true;
              }

              // User doesn't have required role
              router.navigate(['/educator/unauthorized'], { state: { reason: 'unauthorized' } });
              return false;
            }),
            catchError(() => {
              // Error loading roles - deny access
              router.navigate(['/educator/unauthorized'], { state: { reason: 'unauthorized' } });
              return of(false);
            })
          );
        })
      );
    })
  );
};
